import { takeLatest, call, put, select } from "redux-saga/effects";
import TokenService from "utils/auth/Token.service";
import { isConciergeAndVisit, isUnknown, orderByFilterTable, searchVehicleStepsIdByIdentifier, setCookiesCurrentPage } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import { Creators as CreatorsUserSession } from "./../user-session/user-session-duck";
import * as Api from "./location-api";
import { Types, Creators } from "./location-duck";
import { IStructureData } from "./location-model";

export const getFilterFromStateUserSession = (state) => state.UserSession;
export const getLocationFromState = (state) => state.Location;

const redirectPageLocations = () => {
  setCookiesCurrentPage("units");
  window.location.replace("/private/location");
};

export function* onLocationFetchAsync() {
  try {
    const locationResponse = yield call(Api.getLocation);
    const locationList = locationResponse.data;
    const locations = { locationList };
    yield put(Creators.locationsList(locations));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("units_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onLocationActiveFetchAsync() {
  try {
    yield put(Creators.locationsShowLoading());

    const locationActiveResponse = yield call(Api.getLocationActive, { actives: true });
    const locationActiveList = locationActiveResponse.data;
    const locationsActive = { locationActiveList };
    yield put(Creators.locationsActiveList(locationsActive));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("units_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.locationsHideLoading());
  }
}

export function* onLocationActiveFetchPublicAsync() {
  try {
    yield put(Creators.locationsShowLoading());
    const locationActiveResponse = yield call(Api.getLocationActivePublic, { actives: true });
    const locationActiveList = locationActiveResponse.data;
    const locationsActive = { locationActiveList };
    yield put(Creators.locationsActiveList(locationsActive));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("units_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.locationsHideLoading());
  }
}

export function* onProcessesFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    const processesResponse = yield call(Api.getProcesses);
    const processList = processesResponse.data.map((x) => ({
      ...x,
      active: false,
    }));
    yield put(Creators.locationsProcessList(processList));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_processes");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onSaveLocationAsync(action: any) {
  const tokenService = new TokenService();
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  if (action.body.processes === undefined || action.body.processes.length === 0) {
    const registrationErrorWithoutProcess = yield translate("necessary_to_select_process");
    yield YmsNotification({
      type: "error",
      description: registrationErrorWithoutProcess,
    });
  } else {
    try {
      yield put(Creators.locationsShowLoading());
      if (action.id) {
        yield call(Api.updateLocation, {
          ...action.body,
          id: action.id,
        });
        yield put(Creators.locationsShowLoadingLocationEdit());
        yield put(CreatorsUserSession.userSessionUpdateUserData(yield tokenService.updateRefreshToken()));
      } else {
        yield call(Api.createLocation, action.body);
      }
      const registrationSuccessfullyMessage = yield translate("registration_successfully!");
      yield YmsNotification({
        type: "success",
        description: registrationSuccessfullyMessage,
      });
      yield put(Creators.locationsCreateActive(false));
      redirectPageLocations();
    } catch (error) {
      console.error(error);
      const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
      yield YmsNotification({
        type: "error",
        description: registrationErrorMessage,
      });
    } finally {
      yield put(Creators.locationsHideLoading());
      yield put(Creators.locationsHideLoadingLocationEdit());
    }
  }
}

export function* onLocationRegisterFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.locationsShowLoadingTable());
    const location = yield select(getLocationFromState);
    const isAscending = location.filter.ascend === "ascend" ? true : false;
    const orderByFilter = orderByFilterTable(location);
    const resp = yield call(Api.getLocationRegister, {
      filter: location.searchFilter,
      orderBy: orderByFilter,
      ascending: isAscending,
      _size: location.filter.itemsByPage,
      _page: location.filter.pageIndex,
    });
    const locationsData = resp.data;
    yield put(Creators.locationsFetchRegister(locationsData.items, locationsData.totalItems));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("units_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.locationsHideLoadingTable());
  }
}
const getRegionDataFromChild = (childId: number, structuresList: IStructureData[]) => structuresList.flatMap(({ childrens }) => childrens).find(({ id }) => id === childId) || null;

export function* onLocationFetchByIdAsync(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  yield onStructuresFetchAsync();
  const locationState = yield select(getLocationFromState);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.locationsShowLoading());
    yield put(Creators.locationsShowLoadingLocationEdit());
    const resp = yield call(Api.getLocationById, {
      id: action.id,
    });
    const getParent = getRegionDataFromChild(resp.data.structureId, locationState.structuresList);
    yield put(Creators.locationsUpdateItemActive({ ...resp.data, region: getParent.parentId }));
    const processes = resp.data.processes.map((x) => ({
      description: x.description,
      id: x.id,
      vehicleSteps: searchVehicleStepsIdByIdentifier(x.vehicleStepIds),
    }));
    yield put(Creators.locationsUpdateProcessActive(processes));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("units_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.locationsHideLoading());
    yield put(Creators.locationsHideLoadingLocationEdit());
  }
}

export function* onStructuresFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    const structuresResponse = yield call(Api.getStructures);
    const structuresResponseList = structuresResponse.data;
    const structuresList = structuresResponseList.map((item) => ({
      description: item.description,
      id: item.id,
      level: item.level,
      childrens: item.childrens.map((children) => ({
        description: children.description,
        id: children.id,
        level: children.level,
        parentId: item.id,
        childrens: children.childrens,
      })),
    }));
    yield put(Creators.locationsStructuresList({ structuresList }));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_structures");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onStructuresAllFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (isConciergeAndVisit(userSessionState.userData.role)) {
    return;
  }
  try {
    const structuresAllResponse = yield call(Api.getAllStructures);
    const structuresAllList = structuresAllResponse.data;
    yield put(Creators.locationsStructuresAllList({ structuresAllList }));
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_structures");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onLocationByStructureIdFetchAsync(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (isConciergeAndVisit(userSessionState.userData.role)) {
    return;
  }
  try {
    const locationResponse = yield call(Api.getLocationActive, {
      structureId: action.id,
    });
    const locationByStructureIdList = locationResponse.data;
    yield put(Creators.locationsByStructureIdList(locationByStructureIdList));
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("units_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onStructureByLocationFetchAsync(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (isUnknown(userSessionState.userData.role)) {
    return;
  }
  try {
    const structureResponse = yield call(Api.getStructureByLocation, {
      locationId: action.location,
    });
    const locationStructure = structureResponse.data;
    if (locationStructure) {
      yield put(Creators.locationsStructureByLocation(locationStructure));
      yield onLocationByStructureIdFetchAsync({ id: locationStructure.id });
    }
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_structure");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export default function* LocationSagas() {
  yield takeLatest(Types.LOCATION_FETCH_ASYNC, onLocationFetchAsync);
  yield takeLatest(Types.LOCATION_ACTIVE_FETCH_ASYNC, onLocationActiveFetchAsync);
  yield takeLatest(Types.LOCATION_ACTIVE_FETCH_PUBLIC_ASYNC, onLocationActiveFetchPublicAsync);
  yield takeLatest(Types.LOCATION_SAVE_ASYNC, onSaveLocationAsync);
  yield takeLatest(Types.LOCATION_PROCESSES_FETCH_ASYNC, onProcessesFetchAsync);
  yield takeLatest(Types.LOCATIONS_FETCH_REGISTER_ASYNC, onLocationRegisterFetchAsync);
  yield takeLatest(Types.LOCATIONS_FETCH_BY_ID_ASYNC, onLocationFetchByIdAsync);
  yield takeLatest(Types.LOCATION_STRUCTURES_FETCH_ASYNC, onStructuresFetchAsync);
  yield takeLatest(Types.LOCATION_STRUCTURES_ALL_FETCH_ASYNC, onStructuresAllFetchAsync);
  yield takeLatest(Types.LOCATION_BY_STRUCTURE_ID_FETCH_ASYNC, onLocationByStructureIdFetchAsync);
  yield takeLatest(Types.LOCATION_STRUCTURE_BY_LOCATION_FETCH_ASYNC, onStructureByLocationFetchAsync);
}
